import {
  BiCardView,
  BiDashboardOp,
  BiDashboardOpKind,
} from '@euler/model/bi/dashboard';
import { BiPalette, kBiPaletteNames } from '@euler/model/bi/viz';
import { useCallback, useState } from 'react';
import { BiDateChoice } from './biDateRange';
import { BiApplyOutcome } from './useBiDashboard';

/**
 * The page's small writes: remove a card, save the date range, set the
 * palette, rename.
 */
export function useBiDashboardActions(
  apply: (ops: BiDashboardOp[], text: string) => Promise<BiApplyOutcome>,
) {
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  /** No confirm: the undo toast brings the card back with its id. */
  const removeCard = useCallback(
    async (card: BiCardView) => {
      await apply(
        [{ op: BiDashboardOpKind.RemoveCard, cardId: card.id }],
        `已删除「${card.title}」`,
      );
    },
    [apply],
  );

  /** Stores the viewer's pick as the dashboard's own date range. */
  const saveFilters = useCallback(
    async (choice: BiDateChoice): Promise<boolean> => {
      if (choice.kind !== 'override') return false;
      const saved = await apply(
        [
          {
            op: BiDashboardOpKind.SetFilters,
            filters: choice.range ? { dateRange: choice.range } : {},
          },
        ],
        '已保存日期筛选',
      );
      return saved.ok;
    },
    [apply],
  );

  const rename = useCallback(
    async (name: string): Promise<boolean> => {
      setRenaming(true);
      setRenameError(null);
      const saved = await apply(
        [{ op: BiDashboardOpKind.RenameDashboard, name }],
        '已重命名',
      );
      setRenaming(false);
      if (!saved.ok) setRenameError(saved.message);
      return saved.ok;
    },
    [apply],
  );
  const [paletteSaving, setPaletteSaving] = useState(false);
  const setPalette = useCallback(
    async (palette: BiPalette): Promise<boolean> => {
      setPaletteSaving(true);
      try {
        // A refusal shows in the page's toast.
        const saved = await apply(
          [{ op: BiDashboardOpKind.SetPalette, palette }],
          `已换成「${kBiPaletteNames[palette]}」配色`,
        );
        return saved.ok;
      } finally {
        setPaletteSaving(false);
      }
    },
    [apply],
  );

  const clearRenameError = useCallback(() => setRenameError(null), []);

  return {
    removeCard,
    saveFilters,
    setPalette,
    paletteSaving,
    rename,
    renaming,
    renameError,
    clearRenameError,
  };
}
